import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Clock, Calendar, Tag } from 'lucide-react';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { blogPosts } from '@/data';

const Blog = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTag, setSelectedTag] = useState<string | null>(null);
  const [subscribeEmail, setSubscribeEmail] = useState('');
  const [subscribeStatus, setSubscribeStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [subscribeError, setSubscribeError] = useState<string | null>(null);

  // Get all unique tags
  const allTags = Array.from(new Set(blogPosts.flatMap(post => post.tags)));

  // Filter posts based on search term and selected tag
  const filteredPosts = blogPosts.filter(post => {
    const matchesSearch = 
      post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      post.excerpt.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesTag = selectedTag ? post.tags.includes(selectedTag) : true;
    
    return matchesSearch && matchesTag;
  });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };

  return (
    <>
      <Header />
      <main className="pt-32 pb-20">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h1 className="text-4xl md:text-5xl font-bold mb-6">Blog</h1>
            <p className="text-muted-foreground max-w-3xl mx-auto">
              Thoughts, insights, and tutorials on embedded systems development,
              hardware design, and cutting-edge technologies in the field.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.2 }}
            className="mb-12"
          >
            <div className="flex flex-col md:flex-row gap-4 items-center justify-between glass-card p-4">
              <div className="w-full md:w-1/2">
                <Input
                  placeholder="Search articles..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="bg-background"
                />
              </div>
              <div className="flex flex-wrap gap-2 justify-center md:justify-end">
                <Button
                  variant={selectedTag === null ? "default" : "outline"}
                  size="sm"
                  onClick={() => setSelectedTag(null)}
                >
                  All
                </Button>
                {allTags.map((tag) => (
                  <Button
                    key={tag}
                    variant={selectedTag === tag ? "default" : "outline"}
                    size="sm"
                    onClick={() => setSelectedTag(tag)}
                  >
                    {tag}
                  </Button>
                ))}
              </div>
            </div>
          </motion.div>

          <div className="mb-16">
            {filteredPosts.length > 0 ? (
              <motion.div
                variants={containerVariants}
                initial="hidden"
                animate="visible"
                className="grid grid-cols-1 gap-8"
              >
                {filteredPosts.map((post, index) => (
                  <motion.article
                    key={post.id}
                    variants={itemVariants}
                    className={`glass-card overflow-hidden ${
                      index === 0 && post.featured ? "border-primary/30" : ""
                    }`}
                  >
                    <Link to={`/blog/${post.id}`} className="flex flex-col md:flex-row">
                      <div className="md:w-1/3 relative">
                        <img
                          src={post.coverImage}
                          alt={post.title}
                          className="w-full h-full object-cover aspect-video md:aspect-auto"
                        />
                        {post.featured && (
                          <div className="absolute top-4 left-4">
                            <Badge variant="default">Featured</Badge>
                          </div>
                        )}
                      </div>
                      <div className="p-6 md:w-2/3 flex flex-col">
                        <h2 className="text-2xl font-semibold mb-3 hover:text-primary transition-colors">
                          {post.title}
                        </h2>
                        <p className="text-muted-foreground mb-4 line-clamp-3">
                          {post.excerpt}
                        </p>
                        <div className="mt-auto flex flex-wrap gap-2">
                          {post.tags.map((tag) => (
                            <Badge key={tag} variant="outline" className="bg-background/50">
                              {tag}
                            </Badge>
                          ))}
                        </div>
                        <div className="mt-4 flex items-center text-sm text-muted-foreground">
                          <span className="flex items-center">
                            <Calendar className="mr-1 h-4 w-4" />
                            {post.date}
                          </span>
                          <span className="mx-2">•</span>
                          <span className="flex items-center">
                            <Clock className="mr-1 h-4 w-4" />
                            {post.readTime}
                          </span>
                        </div>
                      </div>
                    </Link>
                  </motion.article>
                ))}
              </motion.div>
            ) : (
              <div className="text-center py-12">
                <h3 className="text-xl mb-2">No articles found</h3>
                <p className="text-muted-foreground mb-6">
                  Try adjusting your search or filter to find what you're looking for.
                </p>
                <Button variant="outline" onClick={() => {
                  setSearchTerm('');
                  setSelectedTag(null);
                }}>
                  Clear filters
                </Button>
              </div>
            )}
          </div>

          <div className="text-center">
            <p className="text-muted-foreground mb-4">
              Want to stay updated with the latest articles?
            </p>
            {subscribeStatus === 'success' ? (
              <div className="py-6">
                <h3 className="text-xl font-semibold text-primary mb-2">Thank you for subscribing!</h3>
                <p className="text-muted-foreground">You'll receive updates when new articles are published.</p>
              </div>
            ) : (
              <form
                className="flex max-w-md mx-auto gap-2"
                onSubmit={async (e) => {
                  e.preventDefault();
                  setSubscribeStatus('submitting');
                  setSubscribeError(null);
                  try {
                    const response = await fetch('https://formspree.io/f/xkgrjzln', {
                      method: 'POST',
                      headers: {
                        'Accept': 'application/json',
                        'Content-Type': 'application/json',
                      },
                      body: JSON.stringify({ email: subscribeEmail }),
                    });
                    if (response.ok) {
                      setSubscribeStatus('success');
                      setSubscribeEmail('');
                    } else {
                      setSubscribeStatus('error');
                      setSubscribeError('Something went wrong. Please try again later.');
                    }
                  } catch (err) {
                    setSubscribeStatus('error');
                    setSubscribeError('Something went wrong. Please try again later.');
                  }
                }}
              >
                <Input
                  type="email"
                  name="email"
                  placeholder="Your email address"
                  className="bg-background"
                  required
                  value={subscribeEmail}
                  onChange={e => setSubscribeEmail(e.target.value)}
                  disabled={subscribeStatus === 'submitting'}
                />
                <Button type="submit" disabled={subscribeStatus === 'submitting'}>
                  {subscribeStatus === 'submitting' ? 'Subscribing...' : 'Subscribe'}
                </Button>
              </form>
            )}
            {subscribeStatus === 'error' && subscribeError && (
              <p className="text-red-500 text-sm mt-2">{subscribeError}</p>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
};

export default Blog;
